import React from 'react';
import { Button } from '@/components/ui/button';
import IconProvider from '@/components/ui/IconProvider';

interface ConfirmExistingUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  user: {
    email: string;
    username: string;
    full_name: string;
  };
  isSubmitting?: boolean;
}

export default function ConfirmExistingUserDialog({
  isOpen,
  onClose,
  onConfirm,
  user,
  isSubmitting = false
}: ConfirmExistingUserDialogProps) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && !isSubmitting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onKeyDown={handleKeyDown}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={!isSubmitting ? onClose : undefined}
      />
      
      {/* Dialog */}
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Confirm User Addition
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            disabled={isSubmitting}
          >
            <IconProvider iconstring="fa-times" size={16} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-4">
            <p className="text-center text-sm text-gray-600 dark:text-gray-400">
              This user already exists in LocaNest. Do you want to add them to your company?
            </p>

            {/* User Information Card */}
            <div className="bg-muted rounded-lg p-4 space-y-3 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                  <span className="text-lg font-semibold text-blue-600 dark:text-blue-300">
                    {user.full_name?.charAt(0).toUpperCase() || user.username?.charAt(0).toUpperCase() || user.email.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {user.full_name || 'No name set'}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                    @{user.username}
                  </p>
                </div>
              </div>
              
              <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                <div className="flex items-center space-x-2 text-sm">
                  <IconProvider iconstring="fa-envelope" size={12} className="text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-400 truncate">{user.email}</span>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
              <div className="flex items-start space-x-2">
                <IconProvider iconstring="fa-info-circle" size={14} className="text-blue-600 dark:text-blue-400 mt-0.5" />
                <p className="text-xs text-blue-800 dark:text-blue-200">
                  The user will be added immediately to your company and will receive a notification email.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={onConfirm}
              disabled={isSubmitting}
              className="flex items-center gap-2"
            >
              {isSubmitting && (
                <IconProvider iconstring="fa-spinner" size={14} className="animate-spin" />
              )}
              {isSubmitting ? 'Adding User...' : 'Add User'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

